<template>
  <hb-basic-page
    ref="basicPage"
    :action-buttons="
      $vuetify.breakpoint.smAndDown
        ? actionButtons
        : {}
    "
    :back-button="{ show: true, fallback: 'tenant_start' }"
    :fill-height="true"
    :filters="filters"
    :loading="loading"
    :search-title="$t('tasks.search_title')"
    :subtle-loading="subtleLoading"
    :title="$tc('tasks.title', 2)"
    :title-button="$isReadOnlyUser ? false : titleButton"
    name="tasks-view"
    @handle-search="handleSearch"
  >
    <template v-if="isCreateTaskDisabled" v-slot:append-title-button>
      <plan-chip class="ml-2"/>
    </template>

    <template v-slot:app-bar-buttons>
      <v-btn :disabled="!filteredVotableTasks || filteredVotableTasks.length === 0" :icon="windowWidth < 290"
             :x-small="$vuetify.breakpoint.xsOnly && windowWidth > 289"
             class="px-3" text
             @click="onCopyLinkClick">
        <v-icon :class="windowWidth > 290 ? 'mr-1' : ''" :left="$vuetify.breakpoint.xsOnly && windowWidth > 289">mdi
          mdi-vote
        </v-icon>
        <span class="task-vote-link-text">{{ $t('tasks.copy_task_vote_link') }}</span>
      </v-btn>
    </template>

    <v-col v-if="!loading && Object.keys(groupedTasks).length > 0" v-resize="handleResize"
           class="tasks-main-col fill-height my-0 pb-0"
           cols="12" lg="12"
           md="12"
           xl="12">
      <v-row class="fill-height pl-3 pr-1" style="height: calc(100% + 12px)">
        <div class="left-arrow hidden-md-and-up" @click="scrollPrev">
          <v-icon :size="48">mdi mdi-chevron-left</v-icon>
        </div>
        <div class="right-arrow hidden-md-and-up" @click="scrollNext">
          <v-icon :size="48">mdi mdi-chevron-right</v-icon>
        </div>

        <component
          :is="$vuetify.breakpoint.smAndDown ? 'tasks-container' : 'perfect-scrollbar'"
          id="tasks-category-card-container"
          ref="tasksCategoryCardContainer"
          v-touch="{
            left: () => swipe('next'),
            right: () => swipe('prev'),
          }"
          class="d-flex gap-3 fill-height tasks-category-card-container pr-3"
          style="overflow-x: auto;"
        >
          <template v-for="category in categories">
            <perfect-scrollbar :key="category.id" class="tasks-category-card fill-height">
              <v-card color="white">
                <v-card-title v-if="category" class="tasks-category-card__title pb-0 text-break">
                  <div style="width: 100%">
                    <v-icon style="margin-top: -3px;">mdi mdi-clipboard-list-outline</v-icon>
                    <span :style="{cursor: category.engineName ? 'pointer' : 'default'}"
                          class="ml-1 mr-2"
                          @click="goToCategory(category)">
                      {{ category.nameText }}
                    </span>
                    <v-btn
                      v-if="!$isReadOnlyUser"
                      :disabled="!$isPaidTenant"
                      color="primary"
                      icon
                      outlined
                      style="margin-top: -2px;"
                      x-small
                      @click="
                      taskDialog = true;
                      task = { ...defaultTask, category: category, type: isNaN(category.id) && category.id.endsWith('_help') ? category.id : defaultTask.type };
                    "
                    >
                      <v-icon>mdi mdi-plus</v-icon>
                    </v-btn>
                    <plan-chip v-if="!$isPaidTenant" class="ml-2"/>


                    <div>
                      <div class="text-right" style="width: 100%; font-size: 10px; margin-top: 1px; line-height: 10px;">
                        <span>{{ category.progress }}%</span>
                      </div>
                      <v-progress-linear
                        :color="category.progress < 33 ? 'error' : category.progress < 66 ? 'warning' : 'success'"
                        :value="category.progress"
                        class="rounded"
                        height="3">
                      </v-progress-linear>
                    </div>
                  </div>
                </v-card-title>
                <v-card-text class="py-0">

                </v-card-text>
                <v-card-text class="pb-16">
                  <v-col cols="12">
                    <v-row v-for="task in groupedTasks[category.id]" :key="task.id">
                      <task-card
                        :disabled="$isReadOnlyUser"
                        :task="task"
                        :voteCount="voteCount(task)"
                        @edit="openEditTask"
                        @change-priority="handlePriorityChange"
                        @change-status="handleStatusChange"></task-card>
                    </v-row>
                  </v-col>
                </v-card-text>
              </v-card>
            </perfect-scrollbar>
          </template>
        </component>
      </v-row>
    </v-col>

    <div>
      <basic-dialog
        ref="taskCreateDialog"
        v-model="taskDialog"
        :action-buttons="{
          cancel: {
            text: $t('cancel'),
            icon: 'mdi mdi-close',
            click: () => {
              this.taskDialog = false;
            },
          },
          save: {
            text: $t('save'),
            icon: 'mdi mdi-plus',
            click: saveTask,
          },
        }"
        :loading="loadingSave"
        :title="task.id ? $t('tasks.edit_task') : $t('tasks.add_task')"
      >
        <v-card-text class="scroll-content">
          <div :class="$vuetify.breakpoint.xsOnly ? 'caption' : ''" class="mt-4 mb-6">
            {{ $t('required_fields_are_marked_with_asterisk') }} (<span class="red--text">*</span>).
          </div>
          <v-form ref="taskForm">
            <v-text-field
              v-model="task.name"
              :dense="$vuetify.breakpoint.xsOnly"
              :label="$t('tasks.name')"
              :rules="[v => !!v || $t('tasks.name_required')]"
              class="required"
              outlined
              required
              type="search"
            ></v-text-field>
            <v-textarea v-model="task.description" :label="$t('tasks.description')" outlined required
                        rows="3"></v-textarea>

            <category-select v-if="task.type !== 'personnel_help' && task.type !== 'management_help'"
                             v-model="task.category" :label="$t('tasks.category')"
                             :rules="[v => !!v || $t('tasks.category_required')]"
                             class="required" return-object/>
            <date-picker v-model="task.dueDate" :dense="$vuetify.breakpoint.xsOnly" :input-styles="{outlined: true}"
                         :label="$t('tasks.due_date')"
                         full-width hide-details no-title
                         show-week></date-picker>
            <v-select v-model="task.priority" :dense="$vuetify.breakpoint.xsOnly" :items="$taskPriorities"
                      :label="$t('tasks.priority')"
                      class="mt-8"
                      item-text="name"
                      item-value="value" outlined>
              <template v-slot:item="{ item }">
                <div class="d-flex flex-row">
                  <div>
                    <v-icon :class="[color(item.value) + '--text']" class="mr-1">{{ priorityIcon(item.value) }}</v-icon>
                    {{ item.name }}
                  </div>
                </div>
              </template>
              <template v-slot:selection="{ item }">
                <div class="d-flex flex-row">
                  <div>
                    <v-icon :class="[color(item.value) + '--text']" class="mr-1">{{ priorityIcon(item.value) }}</v-icon>
                    {{ item.name }}
                  </div>
                </div>
              </template>
            </v-select>
            <v-divider class="mb-7"/>
            <v-autocomplete
              v-model="task.userInCharge"
              :dense="$vuetify.breakpoint.xsOnly"
              :item-text="item => item.lastName ? [item.firstName, item.lastName].filter(Boolean).join(' ') : item.email"
              :items="users"
              :label="$t('tasks.user_in_charge')"
              :loading="loadingUsers"
              clearable
              item-value="id"
              outlined
              return-object

            >
            </v-autocomplete>
            <v-autocomplete
              v-model="task.team"
              :dense="$vuetify.breakpoint.xsOnly"
              :items="teams"
              :label="$t('tasks.team_in_charge')"
              :loading="loadingTeams"
              clearable
              item-text="name"
              item-value="key"
              outlined
              return-object
            >
            </v-autocomplete>
          </v-form>
        </v-card-text>
        <template v-if="task.id" v-slot:secondary-buttons>
          <basic-button
            v-if="task.id"
            :block="$vuetify.breakpoint.xsOnly"
            :class="$vuetify.breakpoint.xsOnly ? 'my-1' : ''"
            :loading="loadingDelete"
            color="error"
            icon="mdi mdi-trash-can"
            small
            text
            @click="confirmDeleteTask"
          >
            {{ $t('tasks.delete') }}
          </basic-button>
          <basic-button
            v-if="task.status !== 'DONE' && task.id"
            :block="$vuetify.breakpoint.xsOnly"
            :class="$vuetify.breakpoint.xsOnly ? 'my-1' : ''"
            :loading="loadingSave"
            color="success"
            icon="mdi mdi-check"
            small
            text
            @click="
              () => {
                saveTask({ status: 'DONE' });
              }
            "
          >
            {{ $t('tasks.mark_done') }}
          </basic-button>
          <basic-button
            v-if="task.status === 'DONE' && task.id"
            :block="$vuetify.breakpoint.xsOnly"
            :class="$vuetify.breakpoint.xsOnly ? 'my-1' : ''"
            :loading="loadingSave"
            color="warning"
            icon="mdi mdi-check"
            small
            text
            @click="
              () => {
                saveTask({ status: null });
              }
            "
          >
            {{ $t('tasks.mark_undone') }}
          </basic-button>

        </template>
      </basic-dialog>

      <suggested-tasks-dialog ref="chooseTaskDialog" :suggested-tasks="filteredSuggestedTasks"
                              :votable-tasks="filteredVotableTasks"
                              @refresh="reload"
                              @show-task-vote-help="showTaskVoteHelp"
                              @own-task="() => openEditTask({ ...defaultTask })"/>

      <task-vote-help-dialog ref="taskVoteHelpDialog"/>
    </div>

    <v-col v-if="!loading && tasks.length === 0" class="pa-0 fill-height" cols="12" lg="10" md="10" offset-lg="1"
           offset-md="1" offset-xl="2"
           xl="8">
      <v-row align="center" class="fill-height" no-gutters>
        <no-content-view
          :description="$t('tasks.create_first_task_help')"
          :description-extra="$t('tasks.after_task_is_created_help')"
          :function-button="true"
          :function-button-props="{ disabled: isCreateTaskDisabled }"
          :function-button-text="$t('tasks.add_task')"
          :prop-function="newTask"
          :reload-button="false"
          :title="$t('tasks.create_first_task')"
          function-button-icon="add"
          title-image="img/undraw_add_tasks_re_s5yj.svg"
        >
          <template v-slot:functionButton.append>
            <plan-chip v-if="isCreateTaskDisabled" class="ml-2"/>
          </template>
        </no-content-view>
      </v-row>
    </v-col>

    <v-col v-else-if="!loading && Object.keys(groupedTasks).length === 0" class="pa-0 fill-height" cols="12" lg="10"
           md="10"
           offset-lg="1"
           offset-md="1" offset-xl="2"
           xl="8">
      <v-row align="center" class="fill-height" no-gutters>
        <no-content-view
          :description="$t('tasks.there_is_no_tasks_with_selected_filters')"
          :function-button="true"
          :function-button-text="$t('tasks.clear_filters')"
          :prop-function="clearFilters"
          :reload-button="false"
          :title="$t('tasks.no_tasks')"
          function-button-icon="mdi mdi-filter-remove-outline"
          title-image="img/undraw_empty_re_opql.svg"
        />
      </v-row>
    </v-col>

    <template v-slot:filter-content>
      <v-card-text class="body-2 pb-0">
        <div class="my-1 caption">{{ $t('tasks.development_targets') }}</div>
        <v-select v-model="filters.categories.value"
                  :items="richCategories"
                  :placeholder="filters.categories.title"
                  class="mt-3"
                  clearable
                  dense
                  flat
                  item-text="nameTextLabel"
                  item-value="id"
                  multiple
                  persistent-placeholder
                  solo
                  @change="handleActiveStateOnFilterChange(filters.categories)">
          <template v-slot:selection="{ item, index }">
            <v-list-item v-if="filters.categories.value.length === 1 && index === 0">
              <v-list-item-content class="d-md-flex justify-md-space-between align-center flex-md-grow-1 py-2">
                <div>
                  {{ item.nameTextLabel }}
                </div>
                <div class="d-md-flex justify-md-end">
                  <template v-if="item.type">
                    <template v-for="t in item.type">
                      <v-chip :key="t" x-small>
                        {{ $categoryTypes.find(ct => ct.value === t).text }}
                      </v-chip>
                    </template>
                  </template>
                </div>
              </v-list-item-content>
            </v-list-item>

            <span v-if="filters.categories.value.length > 1 && index === 0">
              {{ $t('tasks.filters.categories_selected', {count: filters.categories.value.length}) }}
            </span>
          </template>
          <template v-slot:prepend>
            <v-checkbox v-model="filters.categories.active" class="my-n3 mr-n1 pa-0" hide-details small></v-checkbox>
          </template>
          <template v-slot:item="{item, attrs, on}">
            <v-list-item #default="{ active }" v-bind="attrs" v-on="on">
              <v-list-item-action>
                <v-checkbox :input-value="active" :ripple="false"></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  <v-row align="center" class="d-md-flex justify-md-space-between align-center flex-md-grow-1 py-2"
                         no-gutters>
                    <div>{{ item.nameTextLabel }}</div>

                    <div class="d-md-flex justify-md-end">
                      <template v-if="item.type">
                        <template v-for="t in item.type">
                          <v-chip :key="t" x-small>
                            {{ $categoryTypes.find(ct => ct.value === t).text }}
                          </v-chip>
                        </template>
                      </template>
                    </div>
                  </v-row>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-select>

        <div class="my-1 caption">{{ filters.assignees.title }}</div>
        <v-select v-model="filters.assignees.value"
                  :item-text="item => item.lastName ? [item.firstName, item.lastName].filter(Boolean).join(' ') : item.email"
                  :items="usersForFilter"
                  :placeholder="filters.assignees.title"
                  class="mt-3"
                  clearable
                  dense
                  flat
                  item-value="id"
                  multiple
                  persistent-placeholder
                  solo
                  @change="handleActiveStateOnFilterChange(filters.assignees)">
          <template v-slot:selection="{ item, index }">
            <span v-if="filters.assignees.value.length === 1 && index === 0">
              {{ item.lastName ? [item.firstName, item.lastName].filter(Boolean).join(' ') : item.email }}
            </span>
            <span v-if="filters.assignees.value.length > 1 && index === 0">
              {{ $t('tasks.filters.assignees_selected', {count: filters.assignees.value.length}) }}
            </span>
          </template>
          <template v-slot:prepend>
            <v-checkbox v-model="filters.assignees.active" class="my-n3 mr-n1 pa-0" hide-details small></v-checkbox>
          </template>
        </v-select>

        <div class="my-1 caption">{{ filters.teams.title }}</div>
        <v-select v-model="filters.teams.value"
                  :item-text="item => item.name"
                  :items="teams"
                  :placeholder="filters.teams.title"
                  class="mt-3"
                  clearable
                  dense
                  flat
                  item-value="key"
                  multiple
                  persistent-placeholder
                  solo
                  @change="handleActiveStateOnFilterChange(filters.teams)">
          <template v-slot:selection="{ item, index }">
            <span v-if="filters.teams.value.length === 1 && index === 0">
              {{ item.name }}
            </span>
            <span v-if="filters.teams.value.length > 1 && index === 0">
              {{ $t('tasks.filters.teams_selected', {count: filters.teams.value.length}) }}
            </span>
          </template>
          <template v-slot:prepend>
            <v-checkbox v-model="filters.teams.active" class="my-n3 mr-n1 pa-0" hide-details small></v-checkbox>
          </template>
        </v-select>

        <div class="my-1 caption">{{ $t('years') }}</div>
        <v-select v-model="filters.years.value"
                  :items="relevantYears"
                  :placeholder="filters.years.title"
                  class="mt-3"
                  clearable
                  dense
                  flat
                  multiple
                  persistent-placeholder
                  solo
                  @change="handleActiveStateOnFilterChange(filters.years)">
          <template v-slot:selection="{ item, index }">
            <template v-if="filters.years.value.length < 4">
              <span>
                <template v-if="index > 0">, </template>
                {{ item }}
              </span>
            </template>
            <template v-else>
              <span v-if="index === 0">
                {{ $t('tasks.filters.years_selected', {count: filters.years.value.length}) }}
              </span>
            </template>
          </template>
          <template v-slot:prepend>
            <v-checkbox v-model="filters.years.active" class="my-n3 mr-n1 pa-0" hide-details small></v-checkbox>
          </template>
        </v-select>
        <v-switch v-model="filters.hasVotes.active" @change="handleFilterTasksByVotesSwitch">
          <template v-slot:label>
            <div>{{ $t('tasks.filters.filter_by_votes') }}</div>
          </template>
        </v-switch>
        <v-switch v-model="filters.status.value">
          <template v-slot:label>
            <div>{{ $t('tasks.filters.filter_done_tasks') }}</div>
          </template>
        </v-switch>
        <v-switch v-model="filterOwnOnly">
          <template v-slot:label>
            <div>{{ $t('tasks.filters.only_mine') }}</div>
          </template>
        </v-switch>
      </v-card-text>
      <v-divider/>
      <v-card-actions>
        <v-spacer/>
        <basic-button icon="mdi mdi-close" right small text @click="clearFilters">
          {{ $t('tasks.clear_filters') }}
        </basic-button>
      </v-card-actions>
    </template>
  </hb-basic-page>
</template>

<script>
import NoContentView from '@/components/NoContentView';

import taskApi from '@/api/task';
import BasicButton from '@/components/BasicButton';
import orderBy from 'lodash/orderBy';
import TaskCard from '@/components/Tenant/Tasks/TaskCard';
import categoryService from '@/service/category.service';
import BasicDialog from '@/components/BasicDialog';
import tenantApi from '@/api/tenant';
import TasksContainer from '@/components/Tenant/Tasks/TasksContainer';
import SuggestedTasksDialog from "@/components/Tenant/Tasks/SuggestedTasksDialog";
import staticTaskApi from "@/api/statictask";
import TranslationService from "@/service/translation.service";
import surveyApi from "@/api/survey";
import dayjs from "dayjs";
import DatePicker from "@/components/DatePicker";
import CategorySelect from "@/components/Category/CategorySelect";
import teamApi from "@/api/team";
import PlanChip from "@/components/Tenant/Reports/PlanChip";
import taskVotingApi from "@/api/taskvoting";
import TaskVoteHelpDialog from "@/components/Tenant/Tasks/TaskVoteHelpDialog";

export default {
  name: 'TasksView',
  components: {
    TaskVoteHelpDialog,
    PlanChip,
    CategorySelect,
    DatePicker, SuggestedTasksDialog, TasksContainer, BasicDialog, TaskCard, BasicButton, NoContentView
  },
  data() {
    this.defaultFilters = {
      assignees: {title: this.$t('tasks.filters.assignees'), value: [], active: false},
      years: {title: this.$t('tasks.filters.year'), value: [], active: false},
      categories: {title: this.$t('tasks.filters.categories'), value: [], active: false},
      teams: {title: this.$t('tasks.team_in_charge'), value: [], active: false},
      hasVotes: {title: this.$t('tasks.task_has_votes'), value: [], active: false},
      status: {title: this.$t('tasks.filters.filter_completed'), value: false},
    }
    return {
      filterOwnOnly: false,
      loading: true,
      loadingUsers: true,
      loadingSave: false,
      loadingTeams: false,
      loadingCopyLink: false,
      subtleLoading: false,
      searchPhrase: '',
      loadingDelete: false,
      tasks: [],
      taskDialog: false,
      task: {},
      taskVotes: [],
      teams: [],
      users: [],
      suggestedTasks: [],
      surveys: [],
      mouseDown: false,
      scrollLeft: 0,
      filters: {...this.defaultFilters},
    };
  },
  computed: {
    windowWidth() {
      return document.body.offsetWidth
    },
    usersForFilter() {
      const users = [...this.users];
      users.unshift({id: -1, email: this.$t('tasks.no_assignee')});
      return users;
    },
    relevantYears() {
      let years = [this.$formatYear(dayjs()), this.$formatYear(dayjs().add(1, 'year'))]
      this.tasks.forEach(t => {
        if (years.findIndex(y => y === this.$formatYear(t.createdAt)) === -1) {
          years.push(this.$formatYear(t.createdAt));
        }
      })
      years.sort((a, b) => a - b);
      return years;
    },
    votedTasks() {
      return this.filteredTaskVotes.filter((tv) => tv.voteCount > 0 && (!['personnel_help', 'management_help'].includes(tv.task.type)))
        .map(tv => tv.task.id);
    },
    titleButton() {
      return {
        text: this.$t('tasks.add_task'),
        icon: 'add',
        callback: this.newTask,
        primary: true,
        disabled: this.isCreateTaskDisabled
      };
    },
    isCreateTaskDisabled() {
      if (this.$isPaidTenant) {
        return false;
      }
      return (
        this.filteredSuggestedTasks?.length === 0 ||
        (this.filteredSuggestedTasks?.length > 0 &&
          this.tasks.filter(t => !['personnel_help', 'management_help'].includes(t.type) && t.staticTaskId).length >= this.$planLimit.suggestedTasks)
      )
    },
    actionButtons() {
      if (window.innerWidth > 359) {
        return {
          left: {
            icon: 'mdi mdi-chevron-left',
            label: '',
            callback: this.scrollPrev,
            primary: false,
          },
          right: {
            icon: 'mdi mdi-chevron-right',
            label: '',
            callback: this.scrollNext,
            primary: false,
          },
          ...(!this.isCreateTaskDisabled && {
            create: {
              icon: 'mdi mdi-plus',
              label: '',
              callback: this.newTask,
              disabled: this.isCreateTaskDisabled,
              primary: true,
            }
          }),
        }
      } else {
        return {
          ...(!this.isCreateTaskDisabled && {
            create: {
              icon: 'mdi mdi-plus',
              label: '',
              callback: this.newTask,
              primary: true,
              disabled: this.isCreateTaskDisabled,
            }
          })
        }
      }
    },
    defaultTask() {
      return {
        type: 'manual',
        priority: 'MEDIUM',
        dueDate: null,
      };
    },

    groupedTasks() {
      let tasks = [...this.tasks];

      if (this.filters.assignees.active) {
        tasks = tasks.filter(task => {
          if (task.userInCharge && this.filters.assignees.value?.includes(task.userInCharge.id)) return true;
          if (!task.userInCharge && this.filters.assignees.value?.includes(-1)) return true;
        });
      }
      if (this.filters.hasVotes.active) {
        tasks = tasks.filter(task => this.votedTasks?.includes(task.id))
      }
      if (this.filters.years.active) {
        tasks = tasks.filter(task => {
          return this.filters.years.value?.includes(this.$formatYear(task.createdAt));
        });
      }
      if (this.filters.teams.active) {
        tasks = tasks.filter(task => {
          if (!task.team) return false;
          return this.filters.teams.value?.includes(task.team.key);
        });
      }
      if (this.filters.categories.active) {
        tasks = tasks.filter(task => {
          return task.category && (
            this.filters.categories.value?.includes(task.category.id) ||
            this.filters.categories.value?.includes(task.category.parentCategory?.id) ||
            this.filters.categories.value?.includes(task.category.parentCategory?.parentCategory?.id)
          )
        });
      }
      if (this.filters.status.value) {
        tasks = tasks.filter(task => task.status !== 'DONE');
      }

      if (this.searchPhrase.length > 0) {
        tasks = tasks.filter(t => t.searchString?.toLowerCase().includes(this.searchPhrase.toLowerCase()));
      }

      tasks.forEach(t => t.priorityOrderNumber = t.priority === 'HIGH' ? 0 : t.priority === 'MEDIUM' ? 1 : 2);
      tasks = orderBy(tasks, ['status', 'priorityOrderNumber'], ['desc', 'asc']);
      const helpCategories = [];
      tasks = tasks.reduce((acc, task) => {
        if (!task.category && task.type.endsWith('_help')) {
          if (!acc[task.type]) {
            acc[task.type] = [];
            helpCategories.push(task.type);
          }
          acc[task.type].push(task);
          return acc;
        } else {
          const mainCategory = categoryService.getMainParentCategory(task.category);
          if (!mainCategory) return acc;
          if (!acc[mainCategory.id]) {
            acc[mainCategory.id] = [];
          }
          acc[mainCategory.id].push(task);
          return acc;
        }
      }, {});
      for (const key in helpCategories) {
        tasks[helpCategories[key]] = orderBy(tasks[helpCategories[key]], ['name', 'status', 'priorityOrderNumber'], ['asc', 'desc', 'asc']);
      }
      return tasks;
    },
    categories() {
      const categories = Object.keys(this.groupedTasks).map(id => {
        let category = categoryService.getCategoryById(parseInt(id));
        if (`${id}`.endsWith('_help')) {
          category = {
            name: id,
            nameText: this.$t('tasks.types.' + id),
            nameTextLabel: this.$t('tasks.types.' + id),
            id: id,
            progress: 0,
          }
        }

        category.progress = Math.round(this.groupedTasks[id].filter(t => t.status === 'DONE').length / this.groupedTasks[id].length * 100);
        return category;
      });
      return categories;
    },
    richCategories() {
      let categories = [...categoryService.richAllCategories];
      categories = categories.flatMap(category => [category, ...(category.level === 1 ? category?.children : [])]);
      categories.forEach(c => {
        c.nameTextLabel = [c.nameText, c?.parentCategory?.nameText, c?.parentCategory?.parentCategory?.nameText]
          .reverse()
          .filter(Boolean)
          .join(' > ');
      });
      return categories;
    },
    filteredSuggestedTasks() {
      let tasks = this.suggestedTasks;
      const categories = categoryService.categories;

      if (!dayjs(this.personnelSurvey?.end).isSameOrBefore(dayjs())) {
        return [];
      }

      tasks = [...tasks.filter(task => {
        if (this.tasks.find(t => t.staticTaskId === task.id))
          return false;
        const categoryPath = [];
        const taskCategory = categoryService.getCategoryById(parseInt(task.categoryId));
        if (taskCategory) {

          let parentCategory = taskCategory;
          categoryPath.push(parentCategory);
          while (parentCategory) {
            if (!parentCategory) break;
            parentCategory = categories.find(c => c.id === parentCategory.parentCategoryId);
            if (parentCategory) categoryPath.push(parentCategory);
          }
          categoryPath.reverse();
          let swot = null;
          try {
            let calculation = this.personnelSurvey?.calculation?.engineResponse?.lta;

            if (categoryPath.length === 2) {
              swot = calculation?.[categoryPath[0].engineName]?.['avg']?.find(c => c.name === categoryPath[1].engineName)?.type
            } else if (categoryPath.length === 3) {
              swot = calculation?.[categoryPath[0].engineName]?.['avg']?.find(c => c.name === categoryPath[1].engineName)?.subdata?.find(c => c.name === categoryPath[2].engineName)?.type
            }
          } catch (e) {
            console.error('Error in filteredSuggestedTasks', e)
          }
          if (swot && task.swotType === swot) {
            return true;
          }
          return false;
        }
      })];
      return tasks;
    },
    personnelSurvey() {
      return this.surveys.find(s => s.type === 'PERSONNEL');
    },
    filteredVotableTasks() {
      const tasks = this.tasks;
      return tasks.filter(task => (!['personnel_help', 'management_help'].includes(task.type) && task.status !== 'DONE'));
    },
    filteredTaskVotes() {
      const taskVotes = this.taskVotes;
      return taskVotes.filter((tv) => !['personnel_help', 'management_help'].includes(tv.task.type))
    },
  },
  methods: {
    filterTasksAssignedToMe(payload) {
      if (payload.value) {
        this.filters = {
          ...this.defaultFilters,
          assignees: {...this.defaultFilters.assignees, value: [this.$userInfo.id], active: true}
        };
      } else {
        this.filters = {
          ...this.defaultFilters,
          assignees: {...this.defaultFilters.assignees, value: [], active: false}
        }
      }
    },
    handleFilterTasksByVotesSwitch() {
      if (this.filters.hasVotes.active) {
        this.filters = {
          ...this.filters,
          hasVotes: {...this.defaultFilters.hasVotes, value: [this.votedTasks], active: true}
        };
      } else {
        this.filters = {
          ...this.filters,
          hasVotes: {...this.defaultFilters.hasVotes, value: [], active: false}
        };
      }
    },
    voteCount(task) {
      let count = 0;
      this.filteredTaskVotes.forEach((tv) => {
        if (tv.task.id === task.id) {
          count += tv.voteCount;
        }
      });
      return count;
    },
    goToCategory(category) {
      if (category.engineName) {
        this.$router.push({
          name: 'tenant_report_category',
          params: {
            category: category.engineName
          }
        });
      }
    },
    clearFilters() {
      Object.keys(this.filters).forEach(key => {
        this.filters[key].active = false
        this.filters[key].value = [];
      });

      this.filters.status.value = false;
      this.filterOwnOnly = false;
      this.searchPhrase = '';
      this.$refs.basicPage.clearSearch();
    },
    handleActiveStateOnFilterChange(filter) {
      if (filter.value && filter.value.length > 0) {
        filter.active = true;
      } else {
        filter.active = false;
      }
    },
    async handlePriorityChange(priority, task) {
      this.task = {...task};
      this.$nextTick(async () => {
        await this.saveTask({priority: priority});
      })
    },
    async handleStatusChange(status, task) {
      this.task = {...task};
      this.$nextTick(async () => {
        await this.saveTask({status: status});
      })
    },
    newTask() {
      if (this.filteredSuggestedTasks.length > 0) {
        this.$refs.chooseTaskDialog.open();
      } else {
        if (this.isCreateTaskDisabled) return;
        this.openEditTask({...this.defaultTask});
      }
    },
    color(priority) {
      if (priority === 'DONE') {
        return 'success';
      }
      return priority === 'HIGH' ? 'error' : priority === 'MEDIUM' ? 'warning' : 'info';
    },
    priorityIcon(priority) {
      return priority === 'HIGH'
        ? 'mdi mdi-chevron-up'
        : priority === 'MEDIUM'
          ? 'mdi mdi-drag-horizontal-variant'
          : 'mdi mdi-chevron-down';
    },
    handleSearch(searchPhrase) {
      this.searchPhrase = searchPhrase;
    },
    openEditTask(task) {
      if (this.$isReadOnlyUser) {
        return;
      }
      this.task = {...task};
      this.taskDialog = true;
    },
    async confirmDeleteTask() {
      const confirm = await this.$confirmDelete(this.$t('tasks.delete'), this.$t('tasks.delete_confirm'));
      if (!confirm) return;
      await this.deleteTask();
    },
    async deleteTask() {
      this.loadingDelete = true;
      try {
        await taskApi.deleteTask(this.task.id);
        this.taskDialog = false;
        this.subtleLoading = true;
        this.tasks = await taskApi.getTasks();
        this.$store.dispatch('reloadMenuCount')
        this.subtleLoading = false;
      } catch (e) {
        this.$handleApiError(e);
      }
      this.loadingDelete = false;
    },
    async saveTask(mergeTask) {
      if (this.$refs.taskForm && !this.$refs.taskForm.validate()) return;
      this.loadingSave = true;
      const task = {...this.task, ...mergeTask};
      if (task.category && task.category.id && isNaN(task.category.id) && task.category.id.endsWith('_help')) {
        task.categoryId = null;
        task.category = null;
      }
      try {
        if (task.id) {
          await taskApi.updateTask(task);
        } else {
          await taskApi.createTask(task);
        }
        this.taskDialog = false;
        this.task = {...this.defaultTask};
        this.subtleLoading = true;
        if (!this.filteredVotableTasks || this.filteredVotableTasks.length === 0) {
          this.showTaskVoteHelp();
        }
        this.tasks = await taskApi.getTasks();
        this.subtleLoading = false;
      } catch (e) {
        console.error(e);
        this.$handleApiError(e, this.$t('tasks.error_saving_task'));
      }
      this.$store.dispatch('reloadMenuCount');
      this.loadingSave = false;
    },
    async reload() {
      if (this.tasks.length > 0) {
        this.subtleLoading = true;
        this.loading = false;
      } else {
        this.subtleLoading = false;
        this.loading = true;
      }
      try {
        const tasks = await taskApi.getTasks();
        tasks.forEach(task => {
          const searchString = [task.name, task.description, task.userInCharge?.email, task.userInCharge?.firstName, task.userInCharge?.lastName, task.category?.nameText, task.category?.parentCategory?.nameText, task.category?.parentCategory?.parentCategory?.nameText, this.$formatYear(task.createdAt)].filter(Boolean).join(' ').toLowerCase();
          task.searchString = searchString;
        })
        this.tasks = tasks;
      } catch (error) {
        this.$handleApiError(error, this.$t('errors.error_loading_tasks'));
      }
      await this.getUsers();
      this.loading = false;
      this.subtleLoading = false;
      this.$nextTick(() => {
        this.initMouseEvents();
      });
      this.reloadSuggestedTasks();
      this.$store.dispatch('reloadMenuCount');

    },
    handleResize() {
      this.initMouseEvents();
    },
    initMouseEvents() {
      if (this.$vuetify.breakpoint.smAndDown) return;
      if (!this.$refs.tasksCategoryCardContainer) return;
      if (this.$refs.tasksCategoryCardContainer.$el.getAttribute('listeners') === 'true') return;

      const mouseDownHandler = (e) => {
        const container = this.$refs.tasksCategoryCardContainer.$el;
        if (e.target.classList) {
          if (e.target.classList.contains('tasks-category-card-container') || e.target.classList.contains('tasks-category-card')) {
            this.mouseDown = true;
            //this.$refs.tasksCategoryCardContainer.$el.classList.add('active');
            this.startX = e.pageX - container.offsetLeft;
            this.scrollLeft = container.scrollLeft;
          }
        }
      }

      const mouseMoveHandler = (e) => {
        if (!this.mouseDown) return;
        const container = this.$refs.tasksCategoryCardContainer.$el;
        e.preventDefault();
        const x = e.pageX - container.offsetLeft;
        const walk = x - this.startX;
        container.scrollLeft = this.scrollLeft - walk;
      }

      const mouseUpHandler = (e) => {
        if (!this.mouseDown) return;
        e.preventDefault();
        this.mouseDown = false;
      }

      this.$refs.tasksCategoryCardContainer.$el.addEventListener('mousedown', mouseDownHandler, false);
      this.$refs.tasksCategoryCardContainer.$el.addEventListener('mousemove', mouseMoveHandler, false);
      this.$refs.tasksCategoryCardContainer.$el.addEventListener('mouseup', mouseUpHandler, false);
      this.$refs.tasksCategoryCardContainer.$el.setAttribute('listeners', 'true');
    },
    async getUsers() {
      this.loadingUsers = true;
      try {
        this.users = await tenantApi.loadTenantUsers();
      } catch (error) {
        this.$handleApiError(error, this.$t('errors.error_loading_users'));
      }
      this.loadingUsers = false;
    },
    scrollPrev() {
      const {currentCard, cards, container} = this.getScroll();
      if (currentCard) {
        const prevCard = cards.find(c => c.idx === currentCard.idx - 1);
        if (prevCard) {
          container.$el.scrollTo({
            left: prevCard.rect.offsetLeft - 12,
            behavior: 'smooth',
          });
        }
      }
    },
    scrollNext() {
      const {currentCard, cards, container} = this.getScroll();
      if (currentCard) {
        const nextCard = cards.find(c => c.idx === currentCard.idx + 1);
        if (nextCard) {
          container.$el.scrollTo({
            left: nextCard.rect.offsetLeft - 12,
            behavior: 'smooth',
          });
        }
      }
    },
    getScroll() {
      const container = this.$refs.tasksCategoryCardContainer;
      const scrollLeftPosition = container.$el.scrollLeft;
      const cards = container.$children
        .filter(c => c.$el.classList.contains('tasks-category-card'))
        .map((c, i) => {
          const {width} = c.$el.getBoundingClientRect();
          const offsetLeft = c.$el.offsetLeft;
          return {
            idx: i,
            component: c,
            el: c.$el,
            rect: {
              offsetLeft,
              startX: offsetLeft,
              endX: offsetLeft + width,
            },
          };
        });

      const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
      const currentViewportEnd = scrollLeftPosition + width;
      const currentViewportStart = scrollLeftPosition;
      const currentCard = cards.reduce((acc, cur) => {
        const overlapCur = Math.max(0, Math.min(cur.rect.endX, currentViewportEnd) - Math.max(cur.rect.startX, currentViewportStart));
        if (!acc) return cur;
        const overlapAcc = Math.max(0, Math.min(acc.rect.endX, currentViewportEnd) - Math.max(acc.rect.startX, currentViewportStart));
        return overlapCur > overlapAcc ? cur : acc;
      }, null);
      return {currentCard, cards, container};
    },
    swipe(direction) {
      switch (direction) {
        case 'next':
          this.scrollNext();
          break;
        case 'prev':
          this.scrollPrev();
          break;
      }
    },
    async reloadSuggestedTasks() {
      try {
        let tasks = await staticTaskApi.getStaticTasks();
        tasks = tasks.map(task => {
          task.nameText = TranslationService.t(task.nameTid);
          task.descriptionText = TranslationService.t(task.descriptionTid);
          task.searchString = `${task.nameText} ${task.descriptionText}`;
          return task;
        }).filter(t => !(['personnel_help', 'management_help'].includes(t.type)));
        this.suggestedTasks = tasks;
      } catch (e) {
        this.$handleApiError(e, this.$t('errors.error_loading_tasks'));
      }
    },
    async reloadSurveys() {
      try {
        this.surveys = await surveyApi.getSurveyDashboards();
      } catch (e) {
        this.$handleApiError(e, this.$t('errors.error_loading_surveys'));
      }
    },
    async reloadTeams() {
      try {
        this.teams = await teamApi.getTeamsLite();
      } catch (e) {
        this.$handleApiError(e, this.$t('errors.error_loading_teams'));
      }
    },
    async reloadTaskVotes() {
      try {
        this.taskVotes = await taskVotingApi.getVotes();
      } catch (e) {
        this.$handleApiError(e, this.$t('errors.error_loading_votes'))
      }
    },
    async onCopyLinkClick() {
      this.loadingCopyLink = true;
      try {
        await this.copyLink();
      } catch (e) {
        console.error(e)
      }
      this.loadingCopyLink = false;
    },
    async copyLink() {
      if (this.filteredVotableTasks && this.filteredVotableTasks.length > 0) {
        try {
          await this.$copyText(window.location.origin + '/public/#/tasks/vote/' + this.$currentTenant.id);
          this.$showSuccessNotification(this.$t('tasks.link_copied'));
        } catch (e) {
          console.error(e);
        }
        return false;
      }
    },
    log(g) {
      console.log(g)
    },
    showTaskVoteHelp() {
      this.$refs.taskVoteHelpDialog.open();
    }
  },
  watch: {
    groupedTasks() {
      this.$nextTick(() => this.initMouseEvents());
    },
    filterOwnOnly() {
      this.filterTasksAssignedToMe({value: this.filterOwnOnly})
    },
  },

  async mounted() {
    if (this.$router.currentRoute.params.category) {
      this.filters.categories.value = [this.$router.currentRoute.params.category];
      this.filters.categories.active = true;
    }
    await this.reload();
    await Promise.all([this.reloadSurveys(), this.reloadTeams(), this.reloadTaskVotes()]);
  },
};
</script>

<style lang="scss" scoped>
$bg-color: rgba(245, 245, 245, 1);
$arrow-bg-color: rgba(245, 245, 245, 0.5);
$arrow-size: 72px;
.left-arrow,
.right-arrow {
  display: flex;
  display: none;
  //border: 1px solid red;
  border-radius: 50%;
  height: $arrow-size;
  width: $arrow-size;
  position: fixed;
  z-index: 3;
  top: calc(80vh - #{$arrow-size});
  transition: background-color 0.1s ease;
  cursor: pointer;
  background-color: $arrow-bg-color;

  i {
    color: var(--v-secondary-base);
  }

  &:hover {
    background-color: $bg-color;
    //border: 1px solid $bg-color;
    box-shadow: 0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%) !important;

    i {
      color: var(--v-primary-base);
    }
  }
}

.left-arrow {
  justify-content: flex-end;
  left: $arrow-size / 2 * -1;
  padding-right: 0px;
}

.right-arrow {
  justify-content: flex-start;
  right: $arrow-size / 2 * -1;
  padding-left: 0px;
}

.tasks-view {
  padding-bottom: 0;
  padding-top: 0;
}

.task-left-border {
  width: 6px;
  height: calc(100% + 24px);
  background-color: #4caf50;
}

.gap-3 {
  gap: 12px;
}

.tasks-main-col {
  height: calc(100vh - 64px);
  @media #{map-get($display-breakpoints,'sm-and-down')} {
    height: calc(100vh - 112px);
  }
}

.tasks-category-card-container {
}

.tasks-category-card {
  min-width: 430px;
  width: calc(33.3333vw - 24px);
  overflow-y: auto;

  @media #{map-get($display-breakpoints,'xl-only')} {
    width: calc(25vw - 42px);
    min-width: calc(25vw - 42px);
  }
  @media #{map-get($display-breakpoints,'md-and-down')} {
    width: calc(50vw - 42px);
    min-width: calc(50vw - 42px);
  }
  @media #{map-get($display-breakpoints,'sm-and-down')} {
    width: calc(75vw - 42px);
    min-width: calc(75vw - 42px);
  }
  @media #{map-get($display-breakpoints,'xs-only')} {
    width: calc(100vw - 42px);
    min-width: calc(100vw - 42px);
  }

  &__title {
    position: sticky;
    top: 0;
    background-color: #ffffff;
    z-index: 1;
  }
}
</style>

<style lang="scss">
.scroll-content {
  max-height: calc(100vh - 240px);
  overflow: auto;
}

@media screen and (min-width: 600px) {
  .scroll-content {
    max-height: calc(100vh - 300px);
    overflow: auto;
  }
}

@media screen and (width < 290px) {
  .task-vote-link-text {
    display: none;
  }
}

.tasks-view {
  //overflow: hidden;

  .ps__rail-y {
    z-index: 2;
  }
}
</style>
